import {
  json,
  redirect,
  type HeadersFunction,
  type LoaderFunctionArgs,
  type MetaFunction,
} from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { abCmsCookie } from '@app/cookies.server'
import { APP_LOCALES, type AppLocales } from '@constants/i18n'
import { getCountryLanguageFromLocale } from '@helpers/getCountryLanguageFromLocale'
import { generatePageMeta } from '@helpers/generatePageMeta'
import { arrayIncludes } from '@helpers/arrayIncludes'
import { getLandingPage } from '@cms/getLandingPage'
import { getCanonicalUrl } from '@helpers/getCanonicalUrl'
import { logger } from '@helpers/core/logger'
import {
  INTERNAL_SERVER_ERROR,
  NOT_FOUND,
} from '@constants/responseErrorsMessages'
import { LandingPage } from '@pages/LandingPage'
import { config } from '@infrastructure/config'
import { getLatestPressReleases } from '@cms/data/getLatestPressReleases'
import { getLandingPageExperimentData } from '@helpers/getLandingPageExperimentData'
import { useActivateOptimizelyDecision } from '@hooks/useActivateOptimizelyDecision'
import { getCacheControlHeader } from '@helpers/getCacheControlHeader'

export type LandingPageLoader = UnwrapLoaderData<typeof loader>

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
  const { locale } = params
  if (!locale || !arrayIncludes(APP_LOCALES, locale)) return []

  const { country, language } = getCountryLanguageFromLocale(locale)

  const { landingPage, websiteUrl, experiment } = data as LandingPageLoader

  const { landingPageData } = landingPage || {}

  // Avoiding destructuring undefined
  const seoData = landingPageData?.seoData || {}
  const isIndexed = landingPageData?.isIndexed || false

  const canonicalUrl = getCanonicalUrl({
    slug: params.slug || '', // landing pages don't have a prefix in the url
    country,
    language,
    baseUrl: websiteUrl,
    experiment: {
      isVariant: experiment?.isVariant,
      controlPageSlug: experiment?.controlPageSlug,
    },
  })
  const { ogImage } = seoData || {}

  const pageMeta = generatePageMeta({
    ...(seoData || {}),
    ogImage: {
      url: ogImage?.url,
    },
    shouldIndex: isIndexed ?? false,
    ogUrl: canonicalUrl,
  })

  const metaDescription = {
    name: 'description',
    content: seoData?.ogDescription ?? '',
  }

  const canonical = {
    tagName: 'link',
    rel: 'canonical',
    href: canonicalUrl,
  }

  return [canonical, ...pageMeta, metaDescription]
}

export const loader = async ({
  params,
  request,
  context,
}: LoaderFunctionArgs) => {
  try {
    const { locale } = params
    const { uniqueDeviceToken } = context
    const landingPage = await getLandingPage(params, request)
    const { websiteUrl, appDownloadUrl } = config

    if (!landingPage) {
      const cacheControl = getCacheControlHeader({
        request,
        responseStatus: 404,
      })

      throw new Response(NOT_FOUND, {
        status: 404,
        headers: { ...cacheControl },
      })
    }

    const { experimentKey, redirectPath, controlPageSlug } =
      await getLandingPageExperimentData({
        landingPage,
        locale: locale as AppLocales,
        request,
        uniqueDeviceToken,
      })

    if (redirectPath) {
      return redirect(redirectPath, {
        headers: {
          'Cache-Control': 'no-store',
        },
      })
    }

    const modifiedQrCodeCta = {
      ...landingPage.landingPageData.qrCodeCta,
      appDownloadData: {
        websiteUrl,
        appDownloadUrl,
        requestUrl: request.url,
        ch: landingPage.landingPageData.trackingName,
        pr: landingPage.landingPageData.productPreference,
      },
    }
    const landingPageWithModifiedQRCodeCta = {
      ...landingPage,
      landingPageData: {
        ...landingPage.landingPageData,
        qrCodeCta: { ...modifiedQrCodeCta },
      },
    }

    let landingPageWithModifiedQRCodeCtaData: typeof landingPageWithModifiedQRCodeCta & {
      latestPressReleases?: Awaited<ReturnType<typeof getLatestPressReleases>>
    } = { ...landingPageWithModifiedQRCodeCta }
    let responseHeaders = {}

    // If variantCookieData is a value, we need to set the variant
    // cookie with the updated value
    if (landingPageWithModifiedQRCodeCtaData.variantCookieData) {
      responseHeaders = {
        'Set-Cookie': await abCmsCookie.serialize(
          landingPageWithModifiedQRCodeCtaData.variantCookieData
        ),
      }
    }

    const hasLatestPressReleases =
      landingPageWithModifiedQRCodeCtaData.landingPageData?.content?.find(
        module => module?.type === 'LatestPressReleases'
      )

    // If we are fetching the LatestPressReleases module
    // we need to do an extra request to get the press releases
    if (hasLatestPressReleases) {
      const latestPressReleases = await getLatestPressReleases(params, request)

      landingPageWithModifiedQRCodeCtaData = {
        ...landingPageWithModifiedQRCodeCtaData,
        latestPressReleases,
      }
    }

    const cacheControl = getCacheControlHeader({
      request,
      hasExperiment: Boolean(experimentKey),
    })

    return json(
      {
        landingPage: landingPageWithModifiedQRCodeCtaData,
        websiteUrl: config.websiteUrl,
        experiment: {
          experimentKey,
          redirectPath,
          isVariant: landingPage.landingPageData.isLpVariant,
          controlPageSlug,
        },
      },
      {
        headers: {
          ...responseHeaders,
          ...cacheControl,
        },
      }
    )
  } catch (error) {
    // Just a not found page, we don't have to log this
    if (error instanceof Response && error.status === 404) {
      throw error
    }

    logger.error('LANDING_PAGE_LOADER_ERROR', {
      error,
      params,
      url: request.url,
    })

    throw new Response(INTERNAL_SERVER_ERROR, { status: 500 })
  }
}

export const headers: HeadersFunction = ({ loaderHeaders, errorHeaders }) => ({
  'Cache-Control':
    errorHeaders?.get('Cache-Control') ||
    loaderHeaders.get('Cache-Control') ||
    'no-store',
})

export default function Index() {
  const { landingPage, experiment } = useLoaderData() as LandingPageLoader
  const { experimentKey } = experiment || {}

  useActivateOptimizelyDecision({
    flagKey: experimentKey,
  })

  return (
    <>{landingPage && <LandingPage loaderDataLandingPage={landingPage} />}</>
  )
}
